import { createFileRoute } from "@tanstack/react-router";
import { TopHeader } from "@/components/TopHeader";
import { BottomNavigation } from "@/components/BottomNavigation";
import { MapView } from "@/components/MapView";
import { CompareBar } from "@/components/CompareBar";
import { MobileAuthGate, useMobileAuthWall } from "@/components/MobileAuthGate";

/** Map viewport persisted in the URL so a refresh restores the same view. */
export interface MapSearch {
  lat?: number;
  lng?: number;
  z?: number;
  /** `false` disables marker clustering; omitted means clustering is on. */
  cluster?: false;
  /** Number of synthetic listings to add for map performance testing. */
  stress?: number;
  /** Currently selected listing, so a refresh reopens the same marker. */
  sel?: string;
}

function num(value: unknown): number | undefined {
  if (value === undefined || value === null || value === "") return undefined;
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
}

export const Route = createFileRoute("/")({
  validateSearch: (raw: Record<string, unknown>): MapSearch => {
    const lat = num(raw.lat);
    const lng = num(raw.lng);
    const z = num(raw.z);
    const cluster = raw.cluster;
    const stress = num(raw.stress);
    const safeLat = lat !== undefined && lat >= -90 && lat <= 90 ? lat : undefined;
    const safeLng = lng !== undefined && lng >= -180 && lng <= 180 ? lng : undefined;
    // A half-specified centre is meaningless — fall back to the default view.
    const paired = safeLat !== undefined && safeLng !== undefined;
    const sel = typeof raw.sel === "string" ? raw.sel.trim().slice(0, 64) : undefined;
    return {
      lat: paired ? safeLat : undefined,
      lng: paired ? safeLng : undefined,
      z: z !== undefined ? Math.min(18, Math.max(4, Math.round(z))) : undefined,
      cluster: cluster === false || cluster === "false" ? false : undefined,
      stress: stress !== undefined && stress > 0 ? Math.min(20000, Math.round(stress)) : undefined,
      sel: sel ? sel : undefined,
    };
  },

  head: () => ({
    meta: [
      { title: "Landoo — Explore properties on the map" },
      {
        name: "description",
        content:
          "Full-screen interactive map showing houses, apartments, land and commercial properties near you. Filter by purpose, type, radius and price.",
      },
      { property: "og:title", content: "Landoo — Explore properties on the map" },
      {
        property: "og:description",
        content: "GPS-first property discovery. Browse nearby homes, land and commercial spaces.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://landoo.zekto.in/" },
      { property: "og:image", content: "https://landoo.zekto.in/og/landoo-og.jpg" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: "https://landoo.zekto.in/og/landoo-og.jpg" },
    ],
    links: [{ rel: "canonical", href: "https://landoo.zekto.in/" }],
  }),
  component: HomePage,
});

function HomePage() {
  // Phones open on Login / Register; the map is the public landing page on
  // tablet and desktop, where guests may browse freely.
  const { blocked } = useMobileAuthWall();
  if (blocked) return <MobileAuthGate />;
  return (
    <div className="flex min-h-screen flex-col bg-background">
      <TopHeader />
      <MapView />
      <CompareBar className="mb-16 sm:mb-0" />
      <BottomNavigation />
    </div>
  );
}
